import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AuthContext';

/**
 * Route guard for /agency/* pages.
 *
 * Default behaviour (Phase 0 contract — UNCHANGED): block unauthenticated
 * users; let any authenticated user through.
 *
 * Phase 1A addition: optional `permission` prop. When supplied, the route
 * also requires `${permission}:view` (or `${permission}:${action}` when
 * `action` is provided). Uses existing permission keys only. If the user
 * is missing the permission, they are redirected to the dashboard so the
 * URL surface does not render a broken page.
 *
 * Super Admin: the backend evaluator already short-circuits Super Admin
 * via `is_super_admin`, AND the seeded Super Admin account
 * carries the full `*:view` matrix locally — so `canView()` returns true
 * for Super Admin without any client-side bypass. No special-case here.
 */
const ProtectedAdminRoute = ({ children, permission, anyPermissions, action = 'view' }) => {
  const { isAuthenticated, loading, hasPermission } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/agency/login" state={{ from: location }} replace />;
  }

  if (permission && !hasPermission(permission, action)) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  if (anyPermissions && !anyPermissions.some((perm) => hasPermission(perm, action))) {
    return <Navigate to="/agency/dashboard" replace />;
  }

  return children;
};

export default ProtectedAdminRoute;
